import {ref, computed, watch} from 'vue'
import {RoomCode} from '~/utils/types/types'
import {nanoid} from 'nanoid'

export const useRoomTypeForm = (formData: any, props: any) => {
    const isLoading = ref<boolean>(false)
    const isShowVallidationError = ref<boolean>(false)
    const baseFormRef = ref(null)

    // Helper function to decode HTML entities
    const decodeHtmlEntities = (text: string): string => {
        if (!text || typeof text !== 'string') return text

        // Create a temporary element to decode HTML entities
        const textarea = document.createElement('textarea')
        textarea.innerHTML = text
        return textarea.value
    }

    // Helper function to get localized text
    const localized = (obj: any): string => {
        const app = useNuxtApp()
        if (!obj) return ''
        if (typeof obj === 'string') {
            // Clean up any "[object Object]" that might be present in the string and decode HTML entities
            const cleaned = obj.replace('[object Object] ', '').replace('[object Object]', '')
            return decodeHtmlEntities(cleaned)
        }

        const locale = app.$i18n.locale.value
        if (obj[locale]) {
            const result = obj[locale]
            if (typeof result === 'string') {
                const cleaned = result.replace('[object Object] ', '').replace('[object Object]', '')
                return decodeHtmlEntities(cleaned)
            }
            return result
        }

        // Fallback to any available locale
        for (const key in obj) {
            if (obj[key]) {
                const result = obj[key]
                if (typeof result === 'string') {
                    const cleaned = result.replace('[object Object] ', '').replace('[object Object]', '')
                    return decodeHtmlEntities(cleaned)
                }
                return result
            }
        }

        // Return empty string instead of "[object Object]" when no valid locale is found
        return ''
    }

    // Computed properties for field states
    const isFieldReadonly = computed(() => {
        const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
        return isHotelDescriptionMode
    })

    const isFieldDisabled = computed(() => {
        // Disable room code for zero/sustainable room types
        return formData.value?.sustainabilityLevel === 'ZERO' || formData.value?.sustainabilityLevel === 'SUSTAINABLE'
    })

    const isSpecialRoomType = computed(() => {
        return formData.value?.sustainabilityLevel === 'ZERO' || formData.value?.sustainabilityLevel === 'SUSTAINABLE'
    })

    const isSustainableRoomType = computed(() => {
        return formData.value?.sustainabilityLevel === 'SUSTAINABLE'
    })

    const isZeroRoomType = computed(() => {
        return formData.value?.sustainabilityLevel === 'ZERO'
    })

    // Utility computed properties
    const useNumbers = computed(() => {
        return Array.from({length: 20}, (_, index) => index + 1)
    })

    const useNumbersWithZero = computed(() => {
        return Array.from(Array(21).keys())
    })

    const useChildrenNumbers = computed(() => {
        return Array.from(Array(11).keys())
    })

    const useChildrenAges = computed(() => {
        return Array.from(Array(18).keys())
    })

    const useRoomCode = computed(() => {
        const {t} = useI18n()
        const roomCodes = ['OTHER', 'SINGLE', 'DOUBLE', 'TRIPLE', 'QUADRUPLE', 'FAMILY']
        return roomCodes.map(value => ({value: RoomCode[value], title: t(`roomCode.${value}`)}))
    })

    // List of amenities to hide
    const hiddenAmenities = [
        'Minibar',
        'Air conditioning',
        'Refrigerator',
        'Coffee/Tea maker',
        'Hairdryer',
        'Mini-refrigerator',
        'Heating/cooling system',
        'Ceiling fan',
        'Stove',
        'Microwave',
        'Dishwasher',
        'Washer',
        'Bathtub',
        'Bath tub with spray jets',
        'Private pool',
        'Iron',
        'Fireplace',
        'Air filtration',
        'High speed internet connection',
        'Wireless internet connection',
        'Internet',
        'Fax machine',
        'Telephone',
    ].map(a => a.toLocaleLowerCase())

    const useRoomServices = computed(() => {
        const supplierStore = useSupplierStore()
        const allServices = clone(supplierStore.hotelServiceDictionaryInfo)['IN_ROOM'] || []
        // Filter out the amenities that should be hidden
        return allServices.filter(service => !hiddenAmenities.includes(service.name.toLocaleLowerCase()))
    })

    // Validation functions
    const updateMinTotalOccupancy = () => {
        formData.value.minTotalOccupancy = formData.value.minOccupancy
    }

    const notLessThanMaxOccupancy = (prop: string) => {
        const {t} = useI18n()
        return v => v >= formData.value.maxOccupancy || `${t('validation.valid', {prop})}`
    }

    const notMoreThanMaxOccupancy = (prop: string) => {
        const {t} = useI18n()
        return v => v <= formData.value.maxOccupancy || `${t('validation.valid', {prop})}`
    }

    // Capacity update function
    const updateCapacity = () => {
        let capacity
        switch (formData.value.otaCode) {
            case RoomCode.SINGLE:
                capacity = 1
                break
            case RoomCode.DOUBLE:
                capacity = 2
                break
            case RoomCode.TRIPLE:
                capacity = 3
                break
            case RoomCode.QUADRUPLE:
            case RoomCode.FAMILY:
                capacity = 4
                break
            default:
                capacity = 0
        }
        if (capacity) {
            formData.value.minOccupancy = capacity
            formData.value.maxOccupancy = capacity
            formData.value.maxExtraPlace = 0
            formData.value.maxWithoutPlace = 0
            formData.value.minTotalOccupancy = capacity
            formData.value.maxTotalOccupancy = capacity
        }
        if (formData.value.otaCode === RoomCode.FAMILY) {
            formData.value.maxExtraPlace = 2
            formData.value.minTotalOccupancy = 4
            formData.value.maxTotalOccupancy = 6
        }
    }

    // Bed and allocation management functions
    const addRow = (): void => {
        const row = {adults: 0, children: 0, hotelRoomTypeAllocationOptionAges: [], id: nanoid()}
        formData.value.allocationOptions.push(row)
    }

    const removeRow = (id: string): void => {
        formData.value.allocationOptions = formData.value.allocationOptions.filter(item => id !== item.id)
    }

    const updateChildrenAgesRow = (id): void => {
        const currentAllocation = formData.value.allocationOptions.find(item => item.id === id)
        const currentAllocationIndex = formData.value.allocationOptions.findIndex(item => item.id === id)
        const childrenQuantity = currentAllocation.children
        const childrenAgesLength = currentAllocation.hotelRoomTypeAllocationOptionAges.length

        if (childrenQuantity > childrenAgesLength) {
            const additionalChildren = childrenQuantity - childrenAgesLength
            for (let i = 0; i < additionalChildren; i++) {
                formData.value.allocationOptions[currentAllocationIndex].hotelRoomTypeAllocationOptionAges.push({
                    minAge: 0,
                    maxAge: 0,
                })
            }
        } else if (childrenQuantity < childrenAgesLength) {
            formData.value.allocationOptions[currentAllocationIndex].hotelRoomTypeAllocationOptionAges =
                formData.value.allocationOptions[currentAllocationIndex].hotelRoomTypeAllocationOptionAges.slice(
                    0,
                    childrenQuantity
                )
        }
    }

    const addBedRow = alternative => {
        const row = {alternative, betTypeId: null, count: 1, id: nanoid()}
        formData.value.beds.push(row)
    }

    const removeBedRow = (id: string): void => {
        formData.value.beds = formData.value.beds.filter(item => id !== item.id)
    }

    const removeAlternateBedTypes = () => {
        formData.value.beds = formData.value.beds.filter(({alternative}) => !alternative)
    }

    // Computed properties for bed types
    const useIsShowAlternative = computed(() => {
        if (!formData.value?.beds?.length) return false
        return formData.value?.beds.filter(({alternative}) => alternative)?.length > 0
    })

    const hasBedTypes = computed(() => {
        return formData.value?.beds?.filter(({alternative}) => !alternative)?.length > 0
    })

    const hasAlternateBedTypes = computed(() => {
        return formData.value?.beds?.filter(({alternative}) => alternative)?.length > 0
    })

    const hasGuestAllocationOptions = computed(() => {
        return formData.value?.allocationOptions?.length > 0
    })

    return {
        // Refs
        isLoading,
        isShowVallidationError,
        baseFormRef,

        // Functions
        localized,
        updateMinTotalOccupancy,
        notLessThanMaxOccupancy,
        notMoreThanMaxOccupancy,
        updateCapacity,
        addRow,
        removeRow,
        updateChildrenAgesRow,
        addBedRow,
        removeBedRow,
        removeAlternateBedTypes,

        // Computed
        isFieldReadonly,
        isFieldDisabled,
        isSpecialRoomType,
        isSustainableRoomType,
        isZeroRoomType,
        useNumbers,
        useNumbersWithZero,
        useChildrenNumbers,
        useChildrenAges,
        useRoomCode,
        useRoomServices,
        useIsShowAlternative,
        hasBedTypes,
        hasAlternateBedTypes,
        hasGuestAllocationOptions,
        hiddenAmenities,
    }
}
